import {
    useState,
} from "react";

import {
    Dialog,
    Portal,
    Box,
    Text,
    Input,
    InputGroup,
    Button,
    VStack,
    HStack,
    Grid,
    Flex,
    Alert,
} from "@chakra-ui/react";

import {
    UserPlus,
    ShieldCheck,
    X,
    Eye,
    EyeOff,
} from "lucide-react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    toaster,
} from "../../components/toaster";

import ActivityToast from "../../components/ActivityToast";
import {
    registerAction,
} from "../../state/actions/authActions";

const RegisterDriverModal = ({
    open,
    onClose,
}) => {
    const dispatch =
    useDispatch();
    const {
        driverPartner,
    } = useSelector(
        (state) =>
            state.driverPartner
    );
    const {
        loading,
    } = useSelector(
        (state) =>
            state.auth
    );
    const [
    showPassword,
    setShowPassword,
] = useState(false);

    const [
        form,
        setForm,
    ] = useState({
        username: "",
        email: "",
        password: "",
        phone_number: "",
        user_type: "Driver",
        partner_referral_code:
            driverPartner?.partner_referral_code ||
            "",
    });

    const handleChange = (
        field,
        value
    ) => {
        setForm(
            (
                prev
            ) => ({
                ...prev,
                [field]:
                    value,
            })
        );
    };

   const handleSubmit =
    async () => {
        if (
            !form.username ||
            !form.email ||
            !form.password ||
            !form.phone_number
        ) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="error"
                        title="Missing Information"
                        description="Please complete all required fields."
                        timestamp="Just now"
                    />
                ),
            });

            return;
        }

       const payload = {
            ...form,
            phone_number:
                `+267${form.phone_number}`,
            user_type:
                "Driver",
            partner_referral_code:
                driverPartner?.partner_referral_code,
        };

        const result =
            await dispatch(
                registerAction(
                    payload
                )
            );

        if (
            result?.success
        ) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="success"
                        title="Driver Registered"
                        description="An OTP verification code has been sent to the driver's email address."
                        timestamp="Just now"
                    />
                ),
            });

            setForm({
                username: "",
                email: "",
                password: "",
                phone_number: "",
                user_type:
                    "Driver",
                partner_referral_code:
                    driverPartner?.partner_referral_code ||
                    "",
            });

            onClose();
        } else {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="error"
                        title="Registration Failed"
                        description={
                            result?.error ||
                            "Unable to register driver."
                        }
                        timestamp="Just now"
                    />
                ),
            });
        }
    };

    return (
        <Dialog.Root
    open={open}
    onOpenChange={(
        e
    ) => {
        if (
            !e.open
        ) {
            onClose();
        }
    }}
>
    <Portal>
        <Dialog.Backdrop
            bg="blackAlpha.600"
            backdropFilter="blur(4px)"
        />

        <Dialog.Positioner
            p={{
                base: 4,
                md: 6,
            }}
        >
            <Dialog.Content
                w="100%"
                maxW={{
                    base: "95vw",
                    md: "700px",
                }}
                maxH="90vh"
                borderRadius={{
                    base: "18px",
                    md: "24px",
                }}
                overflow="hidden"
            >
                <Dialog.Header
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    px={{
                        base: 4,
                        md: 6,
                    }}
                    py={{
                        base: 4,
                        md: 5,
                    }}
                >
                    <Flex
                        justify="space-between"
                        align="flex-start"
                        gap={4}
                        w="100%"
                    >
                        <HStack
                            gap={3}
                            flex="1"
                            minW={0}
                            align="flex-start"
                        >
                            <UserPlus
                                size={22}
                                color="#2453FF"
                            />

                            <Box flex="1">
                                <Text
                                    fontWeight="700"
                                    fontSize={{
                                        base: "md",
                                        md: "lg",
                                    }}
                                >
                                    Register Driver
                                </Text>

                                <Text
                                    fontSize="sm"
                                    color="gray.500"
                                >
                                    Create a driver account and link it to your recruitment profile.
                                </Text>
                            </Box>
                        </HStack>

                        <Box
                            cursor="pointer"
                            onClick={
                                onClose
                            }
                            flexShrink={0}
                        >
                            <X size={20} />
                        </Box>
                    </Flex>
                </Dialog.Header>

                <Dialog.Body
                    px={{
                        base: 4,
                        md: 6,
                    }}
                    py={{
                        base: 4,
                        md: 6,
                    }}
                    overflowY="auto"
                >
                    <VStack
                        align="stretch"
                        gap={6}
                    >
                        <Alert.Root
                            status="info"
                            borderRadius="12px"
                            bg="#F5F8FF"
                            border="1px solid"
                            borderColor="#D6E4FF"
                        >
                            <Alert.Indicator />

                            <Box>
                                <Text fontWeight="600">
                                    Driver Verification Required
                                </Text>

                                <Text
                                    fontSize="sm"
                                    color="gray.600"
                                >
                                    Once the driver account has been created, provide the driver with their email address and temporary password. They can then log in using the Mogoma mobile app and will be guided through the account activation process before completing onboarding.

                                </Text>
                            </Box>
                        </Alert.Root>

                        <Box>
                            <Text
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                letterSpacing="1px"
                                mb={3}
                            >
                                DRIVER ACCOUNT DETAILS
                            </Text>

                            <Grid
                                templateColumns={{
                                    base: "1fr",
                                    md: "1fr 1fr",
                                }}
                                gap={4}
                            >
                                {/* Username */}

                                <Box>
                                    <Text
                                        mb={2}
                                        fontSize="sm"
                                        fontWeight="600"
                                    >
                                        Username
                                    </Text>

                                    <Input
                                        value={
                                            form.username
                                        }
                                        placeholder="driver.username"
                                        onChange={(
                                            e
                                        ) =>
                                            handleChange(
                                                "username",
                                                e.target
                                                    .value
                                            )
                                        }
                                    />
                                </Box>

                                {/* Email */}

                                <Box>
                                    <Text
                                        mb={2}
                                        fontSize="sm"
                                        fontWeight="600"
                                    >
                                        Email Address
                                    </Text>

                                    <Input
                                        type="email"
                                        value={
                                            form.email
                                        }
                                        placeholder="driver@email.com"
                                        onChange={(
                                            e
                                        ) =>
                                            handleChange(
                                                "email",
                                                e.target
                                                    .value
                                            )
                                        }
                                    />
                                </Box>

                                {/* Phone */}

                                <Box>
                                    <Text
                                        mb={2}
                                        fontSize="sm"
                                        fontWeight="600"
                                    >
                                        Phone Number
                                    </Text>

                                    <Input
                                    size="lg"
                                    placeholder="71234567"
                                    value={
                                        form.phone_number
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        handleChange(
                                            "phone_number",
                                            e.target.value
                                                .replace(
                                                    /\D/g,
                                                    ""
                                                )
                                                .slice(0, 8)
                                        )
                                    }
                                    maxLength={8}
                                />
                                </Box>

                                {/* Password */}

                                <Box>
                                    <Text
                                        mb={2}
                                        fontSize="sm"
                                        fontWeight="600"
                                    >
                                        Temporary Password
                                    </Text>

                                    <InputGroup
                                        endElement={
                                            <Box
                                                cursor="pointer"
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword
                                                    )
                                                }
                                            >
                                                {showPassword ? (
                                                    <EyeOff
                                                        size={18}
                                                        color="#64748B"
                                                    />
                                                ) : (
                                                    <Eye
                                                        size={18}
                                                        color="#64748B"
                                                    />
                                                )}
                                            </Box>
                                        }
                                    >
                                        <Input
                                            type={
                                                showPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            value={
                                                form.password
                                            }
                                            placeholder="Create temporary password"
                                            onChange={(
                                                e
                                            ) =>
                                                handleChange(
                                                    "password",
                                                    e.target
                                                        .value
                                                )
                                            }
                                        />
                                    </InputGroup>
                                </Box>
                            </Grid>
                        </Box>

                        <Box>
                            <Text
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                letterSpacing="1px"
                                mb={3}
                            >
                                RECRUITMENT DETAILS
                            </Text>

                            <Grid
                                templateColumns={{
                                    base: "1fr",
                                    md: "1fr 1fr",
                                }}
                                gap={4}
                            >
                                <Box>
                                    <Text
                                        mb={2}
                                        fontSize="sm"
                                        fontWeight="600"
                                    >
                                        Recruitment Agent Code
                                    </Text>

                                    <Input
                                        value={
                                            form.partner_referral_code
                                        }
                                        readOnly
                                        bg="#F8FAFC"
                                    />
                                </Box>

                                <Box>
                                    <Text
                                        mb={2}
                                        fontSize="sm"
                                        fontWeight="600"
                                    >
                                        Account Type
                                    </Text>

                                    <Input
                                        value="Driver"
                                        readOnly
                                        bg="#F8FAFC"
                                    />
                                </Box>
                            </Grid>

                            <HStack
                                mt={4}
                                p={4}
                                bg="#F8FAFC"
                                borderRadius="12px"
                                align="flex-start"
                            >
                                <ShieldCheck
                                    size={18}
                                    color="#16A34A"
                                />

                                <Text
                                    fontSize="sm"
                                    color="gray.600"
                                >
                                    This driver will automatically be linked to your recruitment profile and become eligible for milestone commissions.
                                </Text>
                            </HStack>
                        </Box>
                    </VStack>
                </Dialog.Body>

                <Dialog.Footer
                    px={{
                        base: 4,
                        md: 6,
                    }}
                    py={{
                        base: 4,
                        md: 5,
                    }}
                    borderTop="1px solid"
                    borderColor="gray.200"
                >
                    <Flex
                        ml="auto"
                        w="100%"
                        gap={3}
                        direction={{
                            base: "column-reverse",
                            sm: "row",
                        }}
                    >
                       <Button
                            flex="1"
                            w="100%"
                            size="lg"
                            minH="44px"
                            variant="outline"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>

                        <Button
                            flex="1"
                            w="100%"
                            size="lg"
                            minH="44px"
                            bg="#2453FF"
                            color="white"
                            _hover={{
                                bg: "#1E46D8",
                            }}
                            loading={loading}
                            loadingText="Registering..."
                            onClick={handleSubmit}
                        >
                            Register Driver
                        </Button>
                    </Flex>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog.Positioner>
    </Portal>
</Dialog.Root>
    );
};

export default RegisterDriverModal;